import React, { useEffect, useMemo, useState } from "react";
import { Link, Navigate, useLocation, useParams } from "react-router-dom";
import { Check, MapPin, Calendar, ArrowRight } from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { useSite, useCompany } from "@/context/SiteContext";
import { useSeo, localBusinessLd, breadcrumbLd, faqLd, graph } from "@/hooks/useSeo";
import { api, catLabel, CATEGORIES } from "@/lib/api";
import LEGACY from "@/lib/legacyRedirects.json";
import { Img, SectionHead, Lightbox, BeforeAfter, VideoCard } from "@/components/site/Media";
import { PageHero, QuoteCta, ProjectCard, Loading } from "@/components/site/Blocks";
import { Builder, ServiceCards, ProcessGrid, ReviewList, GoogleBadge, GoogleLinks } from "./Home";

function Wrap({ children, className = "", testid }) {
  return <section className={`py-16 sm:py-20 ${className}`} data-testid={testid}><div className="mx-auto max-w-[1400px] px-5 lg:px-8">{children}</div></section>;
}

function Gallery({ images, testid = "photo-gallery" }) {
  const [idx, setIdx] = useState(null);
  if (!images?.length) return null;
  return (
    <>
      <div className="columns-2 gap-3 md:columns-3 lg:columns-4 [&>button]:mb-3" data-testid={testid}>
        {images.map((src, i) => (
          <button key={src + i} onClick={() => setIdx(i)} className="ib-zoom block w-full overflow-hidden" aria-label={`Open photo ${i + 1}`} data-testid={`${testid}-item-${i}`}>
            <Img src={src} alt="" size="sm" className="w-full" />
          </button>
        ))}
      </div>
      {idx !== null && <Lightbox images={images} index={idx} onClose={() => setIdx(null)} />}
    </>
  );
}

function Faqs({ faqs }) {
  if (!faqs?.length) return null;
  return (
    <Accordion type="single" collapsible className="border-t border-black/10" data-testid="faq-list">
      {faqs.map((f, i) => (
        <AccordionItem key={i} value={`f${i}`}>
          <AccordionTrigger className="text-left font-display text-lg uppercase" data-testid={`faq-trigger-${i}`}>{f.q || f.question}</AccordionTrigger>
          <AccordionContent className="text-base leading-relaxed text-black/70">{f.a || f.answer}</AccordionContent>
        </AccordionItem>
      ))}
    </Accordion>
  );
}

export function About() {
  const { site } = useSite();
  const company = useCompany();
  useSeo("about");
  if (!site) return <Loading />;
  const a = site.content.about_page || {};
  const blocks = [["Our Story", a.story], ["Our Mission", a.mission], ["Quality-First Philosophy", a.philosophy], ["Local Expertise", a.local], ["Our Construction Approach", a.approach]].filter((b) => b[1]);
  return (
    <main>
      <PageHero eyebrow="About Ironbark" title={a.title} text={site.content.about_home?.title} image={a.hero_image} />
      <Wrap className="bg-white" testid="about-content">
        <div className="grid gap-x-16 gap-y-10 md:grid-cols-2">
          {blocks.map(([t, txt]) => <div key={t}><p className="ib-eyebrow">{t}</p><p className="mt-3 text-lg leading-relaxed text-black/80">{txt}</p></div>)}
        </div>
      </Wrap>
      <Builder b={site.content.builder || {}} />
      <Wrap className="bg-[color:var(--ib-off)]" testid="about-values">
        <SectionHead eyebrow="Our Values" title="What we stand for" />
        <div className="mt-10 grid gap-6 md:grid-cols-3">
          {(site.content.about_home?.values || []).map((v) => <div key={v.title} className="border-t-2 border-[color:var(--ib-orange)] bg-white p-7"><p className="font-display text-xl uppercase">{v.title}</p><p className="mt-2 text-black/70">{v.text}</p></div>)}
        </div>
      </Wrap>
      {a.team_images?.length > 0 && <Wrap className="bg-white" testid="about-team"><SectionHead eyebrow="Our Team" title="The people behind the work" /><div className="mt-10"><Gallery images={a.team_images} testid="team-gallery" /></div></Wrap>}
      <Wrap className="bg-white" testid="about-licensing">
        <div className="flex flex-col items-start gap-4 border border-black/10 p-8 sm:flex-row sm:items-center sm:justify-between">
          <div><p className="ib-eyebrow">Licensing</p><p className="mt-1 font-display text-2xl uppercase">{company.license_label} · License #{company.license}</p><p className="text-sm text-black/60">{company.address}</p></div>
          <Link to="/contact" className="ib-btn ib-btn-primary">Request a free estimate</Link>
        </div>
      </Wrap>
      <QuoteCta />
    </main>
  );
}

export function Services() {
  const { site } = useSite();
  useSeo("services");
  if (!site) return <Loading />;
  return (
    <main>
      <PageHero eyebrow="Services" title="What we build" text="General contracting for custom homes, metal buildings, remodels, additions and more across Southwest Florida." image="/media/hero-living.jpg" />
      <Wrap className="bg-[color:var(--ib-off)]" testid="services-list"><ServiceCards services={site.services} /></Wrap>
      <QuoteCta />
    </main>
  );
}

export function ServiceDetail() {
  const { slug } = useParams();
  const { site } = useSite();
  const [s, setS] = useState(null);
  useEffect(() => { setS(null); api.get(`/public/c/services/${slug}`).then((r) => setS(r.data)).catch(() => setS(false)); }, [slug]);
  useSeo("services", s ? { title: s.seo_title || `${s.title} in Southwest Florida | Ironbark Construction`, description: s.seo_description || s.short, image: s.hero_image,
    jsonLd: { "@context": "https://schema.org", "@type": "Service", name: s.title, provider: { "@type": "GeneralContractor", name: "Ironbark Construction" }, areaServed: "Southwest Florida", description: s.short } } : {});
  if (s === null || !site) return <Loading />;
  if (!s) return <NotFound />;
  const related = site.projects.filter((p) => (site.projects && p.category) && (s.slug === "metal-buildings" ? p.category === "metal-buildings" : s.slug === "home-remodeling" ? p.category === "remodeling" : s.slug === "additions-exteriors" ? p.category === "additions" || p.category === "custom-homes" : p.category === "custom-homes")).slice(0, 3);
  return (
    <main>
      <PageHero eyebrow="Services" title={s.title} text={s.short} image={s.hero_image} />
      <Wrap className="bg-white" testid="service-detail">
        <div className="grid gap-12 lg:grid-cols-[1.4fr_1fr]">
          <div><p className="text-lg leading-relaxed text-black/80" data-testid="service-description">{s.description}</p></div>
          <ul className="space-y-3 bg-[color:var(--ib-off)] p-8">
            <p className="ib-eyebrow mb-4">What's included</p>
            {(s.bullets || []).map((b) => <li key={b} className="flex gap-3"><Check className="mt-0.5 shrink-0 text-[color:var(--ib-orange)]" size={18} />{b}</li>)}
            <Link to={`/contact?service=${encodeURIComponent(s.title)}`} className="ib-btn ib-btn-primary mt-6 w-full justify-center" data-testid="service-quote-btn">Request a quote</Link>
          </ul>
        </div>
      </Wrap>
      {s.gallery?.length > 0 && <Wrap className="bg-[color:var(--ib-off)]" testid="service-gallery"><SectionHead eyebrow="Our Work" title="Project photos" /><div className="mt-10"><Gallery images={s.gallery} /></div></Wrap>}
      {s.faqs?.length > 0 && <Wrap className="bg-white" testid="service-faqs"><SectionHead eyebrow="FAQ" title="Common questions" /><div className="mx-auto mt-8 max-w-3xl"><Faqs faqs={s.faqs} /></div></Wrap>}
      {related.length > 0 && <Wrap className="bg-[color:var(--ib-off)]" testid="service-related-projects"><SectionHead eyebrow="Related Projects" title="See it built" /><div className="mt-10 grid gap-4 md:grid-cols-3">{related.map((p) => <ProjectCard key={p.id} p={p} />)}</div></Wrap>}
      <QuoteCta title={`Planning a ${s.title.toLowerCase()} project?`} />
    </main>
  );
}

export function Projects() {
  const { site } = useSite();
  const [cat, setCat] = useState("all");
  const [tab, setTab] = useState("projects");
  const [photos, setPhotos] = useState(null);
  useSeo("projects");
  useEffect(() => { if (tab === "photos" && !photos) api.get("/public/gallery").then((r) => setPhotos(r.data)); }, [tab, photos]);
  const list = useMemo(() => (site?.projects || []).filter((p) => cat === "all" || p.category === cat), [site, cat]);
  if (!site) return <Loading />;
  const tabs = [["projects", "Projects"], ["photos", "Photo Gallery"], ["videos", "Videos"]];
  return (
    <main>
      <PageHero eyebrow="Projects & Gallery" title="Crafted. Built. Proud." text="A look at homes and buildings completed by Ironbark Construction." image="/media/hero-aerial.jpg" />
      <Wrap className="bg-[color:var(--ib-off)]" testid="projects-page">
        <div className="mb-8 flex gap-6 border-b border-black/10">
          {tabs.map(([k, l]) => <button key={k} onClick={() => setTab(k)} className={`-mb-px border-b-2 pb-3 font-display text-lg uppercase ${tab === k ? "border-[color:var(--ib-orange)] text-[color:var(--ib-orange)]" : "border-transparent text-black/60"}`} data-testid={`projects-tab-${k}`}>{l}</button>)}
        </div>
        {tab === "projects" && (
          <>
            <div className="mb-8 flex flex-wrap gap-2" role="tablist">
              {[{ value: "all", label: "All Projects" }, ...CATEGORIES].map((c) => (
                <button key={c.value} onClick={() => setCat(c.value)} className={`rounded-md px-4 py-2 text-xs font-bold uppercase tracking-widest transition-colors ${cat === c.value ? "bg-[color:var(--ib-charcoal)] text-white" : "bg-white text-black/70 hover:text-[color:var(--ib-orange)]"}`} data-testid={`filter-${c.value}`}>{c.label}</button>
              ))}
            </div>
            {list.length ? <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3" data-testid="projects-grid">{list.map((p) => <ProjectCard key={p.id} p={p} />)}</div>
              : <p className="text-black/60" data-testid="projects-empty">No projects in this category yet.</p>}
          </>
        )}
        {tab === "photos" && (photos ? <Gallery images={photos.map((m) => m.url)} testid="public-photo-gallery" /> : <Loading />)}
        {tab === "videos" && (site.videos.length ? <div className="grid gap-8 md:grid-cols-2 lg:grid-cols-3">{site.videos.map((v) => <VideoCard key={v.id} video={v} />)}</div> : <p className="text-black/60" data-testid="videos-empty">Videos coming soon.</p>)}
      </Wrap>
      <QuoteCta />
    </main>
  );
}

export function ProjectDetail() {
  const { slug } = useParams();
  const { site } = useSite();
  const [p, setP] = useState(null);
  useEffect(() => { setP(null); api.get(`/public/c/projects/${slug}`).then((r) => setP(r.data)).catch(() => setP(false)); }, [slug]);
  useSeo("projects", p ? { title: p.seo_title || `${p.title} | Ironbark Construction`, description: p.seo_description || p.description, image: p.cover } : {});
  if (p === null || !site) return <Loading />;
  if (!p) return <NotFound />;
  const videos = site.videos.filter((v) => v.project_id === p.id);
  const related = site.projects.filter((x) => x.id !== p.id && x.category === p.category).slice(0, 3);
  return (
    <main>
      <PageHero eyebrow={catLabel(p.category)} title={p.title} image={p.cover} />
      <Wrap className="bg-white" testid="project-detail">
        <div className="grid gap-12 lg:grid-cols-[1.5fr_1fr]">
          <div>
            <p className="text-lg leading-relaxed text-black/80" data-testid="project-description">{p.description}</p>
            {p.scope && <><p className="ib-eyebrow mt-8">Scope of work</p><p className="mt-2 leading-relaxed text-black/75">{p.scope}</p></>}
          </div>
          <div className="space-y-4 bg-[color:var(--ib-off)] p-8">
            {p.location && <p className="flex items-center gap-2"><MapPin size={16} className="text-[color:var(--ib-orange)]" />{p.location}</p>}
            {p.year && <p className="flex items-center gap-2"><Calendar size={16} className="text-[color:var(--ib-orange)]" />Completed {p.year}</p>}
            {p.features?.length > 0 && <ul className="space-y-2 pt-2">{p.features.map((f) => <li key={f} className="flex gap-2 text-sm"><Check size={16} className="text-[color:var(--ib-orange)]" />{f}</li>)}</ul>}
            <Link to={`/contact?similar=${encodeURIComponent(p.title)}`} className="ib-btn ib-btn-primary w-full justify-center" data-testid="request-similar-btn">Request a similar project</Link>
          </div>
        </div>
      </Wrap>
      {p.before_after?.length > 0 && <Wrap className="bg-[color:var(--ib-off)]" testid="project-before-after"><SectionHead eyebrow="Transformation" title="Before & after" /><div className="mt-10 grid gap-8 md:grid-cols-2">{p.before_after.map((b, i) => <BeforeAfter key={i} {...b} />)}</div></Wrap>}
      <Wrap className="bg-white" testid="project-gallery-section"><SectionHead eyebrow="Gallery" title="Project photos" /><div className="mt-10"><Gallery images={p.gallery} /></div></Wrap>
      {videos.length > 0 && <Wrap className="bg-[color:var(--ib-off)]" testid="project-videos"><SectionHead eyebrow="Video" title="Walkthrough" /><div className="mt-10 grid gap-8 md:grid-cols-2">{videos.map((v) => <VideoCard key={v.id} video={v} />)}</div></Wrap>}
      {related.length > 0 && <Wrap className="bg-[color:var(--ib-off)]" testid="related-projects"><SectionHead eyebrow="More work" title="Related projects" /><div className="mt-10 grid gap-4 md:grid-cols-3">{related.map((x) => <ProjectCard key={x.id} p={x} />)}</div></Wrap>}
      <QuoteCta title="Love this project?" text="Tell us about yours — we'll help you plan something similar." label="Request a similar project" to={`/contact?similar=${encodeURIComponent(p.title)}`} />
    </main>
  );
}

export function Process() {
  const { site } = useSite();
  useSeo("process");
  if (!site) return <Loading />;
  return (
    <main>
      <PageHero eyebrow="Our Process" title="From consultation to completion" text="A clear, collaborative process with honest communication at every step." image="/media/hero-framing.jpg" />
      <Wrap className="bg-[color:var(--ib-off)]" testid="process-page"><ProcessGrid steps={site.process_steps} /></Wrap>
      {site.faqs.length > 0 && <Wrap className="bg-white" testid="process-faqs"><SectionHead eyebrow="FAQ" title="Frequently asked questions" /><div className="mx-auto mt-8 max-w-3xl"><Faqs faqs={site.faqs.filter((f) => f.category === "Timeline & Process").concat(site.faqs.filter((f) => f.category !== "Timeline & Process")).slice(0, 6)} /><Link to="/faq" className="ib-btn ib-btn-outline mt-8" data-testid="process-all-faqs-link">See all FAQs</Link></div></Wrap>}
      <QuoteCta />
    </main>
  );
}

export function Reviews() {
  const { site } = useSite();
  useSeo("reviews");
  if (!site) return <Loading />;
  return (
    <main>
      <PageHero eyebrow="Reviews" title="What our clients say" image="/media/hero-bath.jpg" />
      <Wrap className="bg-[color:var(--ib-off)]" testid="reviews-page"><div className="mb-10 flex justify-center"><GoogleBadge large /></div>{site.reviews.length > 0 && <ReviewList reviews={site.reviews} />}<GoogleLinks /></Wrap>
      <QuoteCta />
    </main>
  );
}

const areaIntro = (c, n) => `${c.name} is a licensed Florida general contractor (Lic. #${c.license}) based in ${c.city}, FL, serving homeowners and property owners in ${n}, FL${c.experience ? `. ${c.president} brings ${c.experience}` : ""}. We build new custom homes and metal buildings, and handle home remodeling, additions and exteriors. Every project is guided with the direct involvement of our President, ${c.president}.`;

const areaFaqs = (c, n, faqs) => [
  { question: `Does ${c.name} serve ${n}, FL?`, answer: `Yes. ${n} is one of our service areas. ${c.name} is based in ${c.city}, FL and builds custom homes, metal buildings, remodels and additions in ${n}. Call ${c.phone} for a free estimate.` },
  ...faqs.slice(0, 5),
];

export function ServiceAreas() {
  const { site } = useSite();
  const c = useCompany();
  const areas = site?.service_areas || [];
  useSeo("home", { title: `Service Areas | General Contractor in ${areas.map((a) => a.name).join(", ")}, FL | Ironbark Construction`, description: c.service_area_summary, jsonLd: graph(breadcrumbLd([["Service Areas", "/service-areas"]])) });
  if (!site) return <Loading />;
  return (
    <main>
      <PageHero eyebrow="Service Areas" title="Where we build" text={c.service_area_summary} image="/media/hero-aerial.jpg" />
      <Wrap className="bg-white" testid="areas-index">
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {areas.map((a) => (
            <Link key={a.id} to={`/service-areas/${a.slug}`} className="group flex items-start gap-3 border border-black/10 p-6 transition-colors hover:border-[color:var(--ib-orange)]" data-testid={`areas-index-${a.slug}`}>
              <MapPin className="mt-1 shrink-0 text-[color:var(--ib-orange)]" size={20} />
              <span><span className="block font-display text-xl uppercase">{a.name}, FL</span><span className="mt-1 block text-sm text-black/60">{a.intro}</span></span>
            </Link>
          ))}
        </div>
      </Wrap>
      <QuoteCta />
    </main>
  );
}

export function ServiceArea() {
  const { slug } = useParams();
  const { site } = useSite();
  const c = useCompany();
  const [a, setA] = useState(null);
  useEffect(() => { setA(null); api.get(`/public/c/service_areas/${slug}`).then((r) => setA(r.data)).catch(() => setA(false)); }, [slug]);
  const faqs = a && site ? areaFaqs(c, a.name, site.faqs || []) : [];
  useSeo("home", a && site ? {
    title: a.seo_title || `Custom Home Builder & General Contractor in ${a.name}, FL | Ironbark Construction`,
    description: a.seo_description || `Licensed general contractor serving ${a.name}, FL — custom homes, metal buildings, remodeling and additions. Free estimates: ${c.phone}.`,
    jsonLd: graph(localBusinessLd(c, [a], site), faqLd(faqs), breadcrumbLd([["Service Areas", "/service-areas"], [a.name, `/service-areas/${a.slug}`]])),
  } : {});
  if (a === null || !site) return <Loading />;
  if (!a) return <NotFound />;
  const others = (site.service_areas || []).filter((x) => x.slug !== a.slug);
  return (
    <main>
      <PageHero eyebrow={`Service Area · ${a.name}, FL`} title={`General Contractor in ${a.name}`} text={a.intro} image={a.image || "/media/hero-aerial.jpg"} />
      <Wrap className="bg-white" testid="area-body">
        <div className="ib-prose max-w-3xl text-lg text-black/80">
          <p data-testid="area-intro">{areaIntro(c, a.name)}</p>
          {a.body && a.body.split("\n\n").map((t, i) => <p key={i}>{t}</p>)}
        </div>
      </Wrap>
      <Wrap className="bg-[color:var(--ib-off)]" testid="area-services"><SectionHead eyebrow={`Services in ${a.name}`} title="What we build" /><div className="mt-10"><ServiceCards services={site.services} /></div></Wrap>
      <Wrap className="bg-white" testid="area-reviews"><SectionHead eyebrow="Customer Reviews" title="What our clients say" /><div className="mt-10"><ReviewList reviews={(site.reviews || []).slice(0, 3)} /></div></Wrap>
      <Wrap className="bg-[color:var(--ib-off)]" testid="area-faqs"><SectionHead eyebrow={`${a.name} FAQ`} title={`Questions from ${a.name} homeowners`} /><div className="mt-8 max-w-3xl"><Faqs faqs={faqs} /></div></Wrap>
      {others.length > 0 && (
        <Wrap className="bg-white" testid="area-others">
          <p className="ib-eyebrow">Also serving</p>
          <div className="mt-4 flex flex-wrap gap-3">{others.map((x) => <Link key={x.id} to={`/service-areas/${x.slug}`} className="ib-btn ib-btn-outline" data-testid={`area-other-${x.slug}`}>{x.name}</Link>)}</div>
        </Wrap>
      )}
      <QuoteCta title={`Planning a project in ${a.name}?`} />
    </main>
  );
}

export function legacyTarget(path, custom = []) {
  const p = decodeURIComponent(path).toLowerCase().replace(/\/+$/, "") || "/";
  const hit = custom.find((r) => r.from && r.from.toLowerCase().replace(/\/+$/, "") === p);
  if (hit) return hit.to;
  if (LEGACY.exact[p]) return LEGACY.exact[p];
  const kw = LEGACY.keywords.find(([k]) => p.includes(k));
  return kw ? kw[1] : "/";
}

export function NotFound({ catchAll = false }) {
  const { site } = useSite();
  const company = useCompany();
  const loc = useLocation();
  useSeo("home", { title: "Page not found | Ironbark Construction", noindex: true });
  if (catchAll && !site) return <Loading />;
  if (catchAll) {
    const to = legacyTarget(loc.pathname, site.settings?.redirects || []);
    if (/^https?:/.test(to)) { window.location.replace(to); return <Loading />; }
    return <Navigate to={to} replace />;
  }
  return (
    <main className="mx-auto flex min-h-[60vh] max-w-xl flex-col items-center justify-center px-5 text-center" data-testid="not-found">
      <p className="ib-eyebrow">404</p>
      <h1 className="mt-2 font-display text-4xl uppercase">Page not found</h1>
      <p className="mt-3 text-black/60">The page you're looking for has moved. Call us at {company.phone} or head back home.</p>
      <Link to="/" className="ib-btn ib-btn-primary mt-6">Back to home <ArrowRight size={16} /></Link>
    </main>
  );
}
